import { useState, useEffect, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import {
  BarChart3,
  TrendingUp,
  HardHat,
  Package,
  Search,
  Building2,
  LogOut,
  Menu,
  X,
  PlusCircle,
  UserPlus,
  RotateCcw,
  XCircle,
  Ship,
  Settings,
  ChevronDown,
  Gem,
  Bell,
  DollarSign,
  HelpCircle,
  Database,
  BoxesIcon,
  PackageOpen,
  ClipboardCheck,
  ArrowRightLeft,
  Scissors,
  Shield,
} from 'lucide-react';
import { useOpenAlertCount } from '@/hooks/useAlerts';
import ChatWidget from '@/components/ChatWidget';

const CHAT_ROLES = ['Owner', 'OperationsManager', 'SuperAdmin'];

type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
};

type NavGroup = {
  label: string;
  icon: React.ElementType;
  children: NavItem[];
};

// Full nav definitions — filtered per role below
const allAnalyticsNav: NavItem[] = [
  { to: '/dashboard', label: 'Overview', icon: BarChart3, end: true },
  { to: '/dashboard/yield', label: 'Yield Trends', icon: TrendingUp },
  { to: '/dashboard/workers', label: 'Workers', icon: HardHat },
  { to: '/dashboard/packages', label: 'Packages', icon: Package },
  { to: '/dashboard/stone', label: 'Stone Search', icon: Search },
  { to: '/dashboard/suppliers', label: 'Suppliers', icon: Building2 },
];

const stockNav: NavItem = { to: '/dashboard/stock', label: 'Stock', icon: BoxesIcon };
const exportsNav: NavItem = { to: '/dashboard/exports', label: 'Exports', icon: PackageOpen };

const shipmentNav: NavItem = {
  to: '/dashboard/new-shipment',
  label: 'New Shipment',
  icon: PlusCircle,
};

const allOperationsChildren: NavItem[] = [
  { to: '/dashboard/assign-polisher', label: 'Assign to Polisher', icon: UserPlus },
  { to: '/dashboard/record-return', label: 'Record Return', icon: RotateCcw },
  { to: '/dashboard/log-rejection', label: 'Log Rejection', icon: XCircle },
  { to: '/dashboard/mark-export', label: 'Mark Export', icon: Ship },
];

const allSettingsChildren: NavItem[] = [
  { to: '/dashboard/settings/suppliers', label: 'Suppliers', icon: Building2 },
  { to: '/dashboard/settings/stone-types', label: 'Stone Types', icon: Gem },
  { to: '/dashboard/settings/workers', label: 'Workers', icon: HardHat },
  { to: '/dashboard/settings/data-quality', label: 'Data Quality', icon: Database },
];

// QC-specific nav items
const qcNav: NavItem[] = [
  { to: '/dashboard/stone', label: 'Stone Search', icon: Search },
  { to: '/dashboard/qc/verify-receipt', label: 'Verify Receipt', icon: ClipboardCheck },
  { to: '/dashboard/qc/handoffs', label: 'Handoff Confirmation', icon: ArrowRightLeft },
  { to: '/dashboard/log-rejection', label: 'Log Rejection', icon: XCircle },
  { to: '/dashboard/record-return', label: 'Record Return', icon: RotateCcw },
];

// Sawer-specific nav items
const sawerNav: NavItem[] = [
  { to: '/dashboard/sawing', label: 'My Stones', icon: Scissors },
];

const roleBadgeColors: Record<string, string> = {
  Owner: 'bg-gold/20 text-gold',
  OperationsManager: 'bg-blue-500/20 text-blue-400',
  QCInspector: 'bg-purple-500/20 text-purple-400',
  Sawer: 'bg-green-500/20 text-green-400',
  Polisher: 'bg-orange-500/20 text-orange-400',
  SuperAdmin: 'bg-purple-600/20 text-purple-300',
};

function NavGroupDropdown({ group, closeSidebar }: { group: NavGroup; closeSidebar: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <group.icon size={18} />
        {group.label}
        <ChevronDown size={14} className={`ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="ml-4 space-y-0.5 border-l border-border/50 pl-3">
          {group.children.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gold/10 text-gold'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NavLinkItem({ item, closeSidebar }: { item: NavItem; closeSidebar: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={closeSidebar}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-gold/10 text-gold'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`
      }
    >
      <item.icon size={18} />
      {item.label}
    </NavLink>
  );
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const role = user?.role || '';

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const { data: openAlertCount } = useOpenAlertCount();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineDismissed, setOfflineDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => { setIsOffline(true); setOfflineDismissed(false); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const closeSidebar = () => setSidebarOpen(false);
  const roleLabel = role === 'OperationsManager' ? 'Ops Manager' : role === 'SuperAdmin' ? 'Super Admin' : role;

  // Build nav based on role
  const { analyticsItems, showStock, showExports, showAlerts, showShipment, operationsGroup, settingsGroup, showCosts, showHelp, qcItems, sawerItems } = useMemo(() => {
    switch (role) {
      case 'SuperAdmin':
      case 'Owner':
        return {
          analyticsItems: allAnalyticsNav,
          showStock: true,
          showExports: true,
          showAlerts: true,
          showShipment: true,
          operationsGroup: { label: 'Operations', icon: Gem, children: allOperationsChildren } as NavGroup,
          settingsGroup: { label: 'Settings', icon: Settings, children: allSettingsChildren } as NavGroup,
          showCosts: true,
          showHelp: true,
          qcItems: null,
          sawerItems: null,
        };
      case 'OperationsManager':
        return {
          analyticsItems: allAnalyticsNav,
          showStock: true,
          showExports: true,
          showAlerts: true,
          showShipment: true,
          operationsGroup: { label: 'Operations', icon: Gem, children: allOperationsChildren } as NavGroup,
          settingsGroup: { label: 'Settings', icon: Settings, children: allSettingsChildren } as NavGroup,
          showCosts: false, // Hidden: Cost Summary, financial margin data
          showHelp: true,
          qcItems: null,
          sawerItems: null,
        };
      case 'QCInspector':
        return {
          analyticsItems: null,
          showStock: false,
          showExports: false,
          showAlerts: false,
          showShipment: false,
          operationsGroup: null,
          settingsGroup: null,
          showCosts: false,
          showHelp: false,
          qcItems: qcNav,
          sawerItems: null,
        };
      case 'Sawer':
        return {
          analyticsItems: null,
          showStock: false,
          showExports: false,
          showAlerts: false,
          showShipment: false,
          operationsGroup: null,
          settingsGroup: null,
          showCosts: false,
          showHelp: false,
          qcItems: null,
          sawerItems: sawerNav,
        };
      default:
        // Polisher shouldn't reach dashboard — they go to /worker
        return {
          analyticsItems: null,
          showStock: false,
          showExports: false,
          showAlerts: false,
          showShipment: false,
          operationsGroup: null,
          settingsGroup: null,
          showCosts: false,
          showHelp: false,
          qcItems: null,
          sawerItems: null,
        };
    }
  }, [role]);

  return (
    <div className="flex h-screen bg-[#0a0a0b]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b border-border px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-gold to-gold-light">
            <svg className="h-4 w-4 text-[#0a0a0b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </div>
          <span className="text-sm font-bold text-gold">Aditi Diamonds</span>
          <button
            className="ml-auto text-muted-foreground lg:hidden"
            onClick={closeSidebar}
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {/* QC Inspector nav */}
          {qcItems && qcItems.map((item) => (
            <NavLinkItem key={item.to} item={item} closeSidebar={closeSidebar} />
          ))}

          {/* Sawer nav */}
          {sawerItems && sawerItems.map((item) => (
            <NavLinkItem key={item.to} item={item} closeSidebar={closeSidebar} />
          ))}

          {/* Analytics (Owner, OpsManager) */}
          {analyticsItems && analyticsItems.map((item) => (
            <NavLinkItem key={item.to} item={item} closeSidebar={closeSidebar} />
          ))}

          {/* Stock */}
          {showStock && <NavLinkItem item={stockNav} closeSidebar={closeSidebar} />}

          {/* Exports */}
          {showExports && <NavLinkItem item={exportsNav} closeSidebar={closeSidebar} />}

          {/* Cost Summary (Owner only) */}
          {showCosts && (
            <NavLinkItem item={{ to: '/dashboard/costs', label: 'Cost Summary', icon: DollarSign }} closeSidebar={closeSidebar} />
          )}

          {/* Alerts */}
          {showAlerts && (
            <NavLink
              to="/dashboard/alerts"
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gold/10 text-gold'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`
              }
            >
              <Bell size={18} />
              Alerts
              {openAlertCount !== undefined && openAlertCount > 0 && (
                <span className="ml-auto rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {openAlertCount}
                </span>
              )}
            </NavLink>
          )}

          {(analyticsItems || showAlerts) && <div className="my-3 h-px bg-border" />}

          {/* New Shipment */}
          {showShipment && <NavLinkItem item={shipmentNav} closeSidebar={closeSidebar} />}

          {/* Operations */}
          {operationsGroup && <NavGroupDropdown group={operationsGroup} closeSidebar={closeSidebar} />}

          {(operationsGroup || settingsGroup) && <div className="my-3 h-px bg-border" />}

          {/* Settings */}
          {settingsGroup && <NavGroupDropdown group={settingsGroup} closeSidebar={closeSidebar} />}

          {settingsGroup && <div className="my-3 h-px bg-border" />}

          {/* Help */}
          {showHelp && (
            <NavLink
              to="/onboarding"
              onClick={closeSidebar}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <HelpCircle size={18} />
              Help
            </NavLink>
          )}
        </nav>

        {/* Admin link (SuperAdmin only) */}
        {role === 'SuperAdmin' && (
          <div className="px-4 pb-2">
            <NavLink
              to="/admin/users"
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-purple-500/10 text-purple-400'
                    : 'text-muted-foreground/60 hover:bg-purple-500/5 hover:text-purple-400'
                }`
              }
            >
              <Shield size={14} />
              Admin
            </NavLink>
          </div>
        )}

        {/* User */}
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <NavLink
              to="/profile"
              onClick={closeSidebar}
              className="flex flex-1 items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-secondary/50"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-bold text-foreground">
                {user?.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 truncate">
                <p className="truncate text-sm font-medium text-foreground">{user?.name}</p>
                <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${roleBadgeColors[role] || 'bg-secondary text-muted-foreground'}`}>
                  {roleLabel}
                </span>
              </div>
            </NavLink>
            <button
              onClick={handleLogout}
              className="text-muted-foreground transition-colors hover:text-danger"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar (mobile) */}
        <header className="flex h-14 items-center gap-4 border-b border-border px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-muted-foreground"
          >
            <Menu size={22} />
          </button>
          <span className="text-sm font-bold text-gold">Aditi Diamonds</span>
        </header>

        {/* Offline banner */}
        {isOffline && !offlineDismissed && (
          <div className="flex items-center justify-between bg-orange-500/10 px-4 py-2 text-sm text-orange-400 border-b border-orange-500/20">
            <span>Offline — changes will sync when connection restored</span>
            <button onClick={() => setOfflineDismissed(true)} className="text-orange-400 hover:text-orange-300">&times;</button>
          </div>
        )}

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* AI assistant — Owner / OpsManager / SuperAdmin only */}
      {CHAT_ROLES.includes(role) && <ChatWidget />}
    </div>
  );
}
